import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  ArrowLeft, 
  MapPin, 
  Users, 
  Bed, 
  Bath, 
  Wifi, 
  Car, 
  Crown, 
  Star,
  Calendar,
  Clock,
  Phone,
  Mail,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Home,
  Maximize,
  Globe,
  Shield,
  Award,
  Camera,
  X,
  Play,
  Grid3X3,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { villasService } from '../services/api';
import ConciergeChat from './ConciergeChat';
import AvailabilityCalendar from './AvailabilityCalendar';
import StayConditions from './StayConditions';
import { getBookedDates } from '../utils/icalParser';

const VillaDetail = ({ initialShowBooking = false }) => {
  const { villaId } = useParams();
  const navigate = useNavigate();
  const [villa, setVilla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showBooking, setShowBooking] = useState(initialShowBooking);
  const [showGallery, setShowGallery] = useState(false);
  const [selectedDates, setSelectedDates] = useState({ checkIn: '', checkOut: '' });
  const [guests, setGuests] = useState(4);
  const [selectedSeason, setSelectedSeason] = useState(0);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const [openAccordions, setOpenAccordions] = useState({
    rental: false,
    reservation: false,
    cancel: false
  });
  const [availability, setAvailability] = useState({
    loading: true,
    bookedDates: [],
    error: null
  });

  // Toggle accordion
  const toggleAccordion = (section) => {
    setOpenAccordions(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Précharger toutes les images de la galerie
  useEffect(() => {
    if (villa && villa.gallery && villa.gallery.length > 0) {
      const preloadImages = async () => {
        const imagePromises = villa.gallery.map((src) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = src;
            img.onload = resolve;
            img.onerror = reject;
          });
        });

        try {
          await Promise.all(imagePromises);
          setImagesPreloaded(true);
          console.log(`✅ ${villa.gallery.length} images préchargées avec succès`);
        } catch (error) {
          console.warn('Certaines images n\'ont pas pu être préchargées:', error);
          setImagesPreloaded(true); // Continuer quand même
        }
      };

      preloadImages();
    }
  }, [villa]);

  useEffect(() => {
    const fetchVillaDetails = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await villasService.getVillaById(villaId);
        
        if (response.success && response.data) {
          const apiVilla = response.data;
          
          // Convertir les données API au format attendu
          const formattedVilla = {
            id: apiVilla.id,
            name: apiVilla.public_name,
            location: `${apiVilla.city}, ${apiVilla.country}`,
            emotionalTagline: apiVilla.short_description ? (() => {
              const textarea = document.createElement('textarea');
              textarea.innerHTML = apiVilla.short_description;
              const decoded = textarea.value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
              return decoded.length > 150 ? decoded.substring(0, 150) + '...' : decoded;
            })() : 'Villa de luxe exceptionnelle',
            propertyType: apiVilla.type_of_house || 'Villa',
            priceDisplay: apiVilla.rates && apiVilla.rates.length > 0 
              ? (() => {
                  const price = parseFloat(apiVilla.rates[0].price);
                  if (isNaN(price)) return 'Sur demande';
                  // Formatage avec espace comme séparateur de milliers
                  const formattedPrice = Math.round(price * 7).toLocaleString('fr-FR').replace(/\s/g, ' ');
                  return `${formattedPrice}€`;
                })()
              : 'Sur demande',
            priceSubtext: 'par semaine',
            capacity: `${apiVilla.number_pax} personnes`,
            bedrooms: parseInt(apiVilla.number_bedrooms) || 0,
            bathrooms: parseInt(apiVilla.number_bathrooms) || 0,
            surface: `${apiVilla.villa_area} m²`,
            terrain: apiVilla.ground_area ? `${apiVilla.ground_area} m²` : 'N/A',
            story: apiVilla.long_description || 'Villa de prestige offrant une expérience unique dans un cadre d\'exception. Cette propriété exclusive combine luxe, confort et intimité pour des séjours inoubliables.',
            storyHTML: apiVilla.long_description ? (() => {
              // Décoder les entités HTML
              const textarea = document.createElement('textarea');
              textarea.innerHTML = apiVilla.long_description;
              return textarea.value;
            })() : null,
            
            // Images
            mainImage: apiVilla.photos && apiVilla.photos.length > 0 
              ? apiVilla.photos[0].photo_url 
              : 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200',
            gallery: apiVilla.photos && apiVilla.photos.length > 0
              ? apiVilla.photos.map(p => p.photo_url)
              : ['https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200'],
            
            // Équipements formatés - UNIQUEMENT de la BDD
            amenities: apiVilla.equipments && apiVilla.equipments.length > 0
              ? apiVilla.equipments.flatMap(cat => 
                  cat.equipement ? cat.equipement : []
                )
              : [], // Pas de fallback - on masque la section si vide
            
            // Tarifs bruts (pour calculs)
            rawRates: apiVilla.rates || [],
            
            // Tarifs par saison - Grouper les tarifs identiques
            pricingSeasons: apiVilla.rates && apiVilla.rates.length > 0
              ? (() => {
                  // Grouper par prix et configuration
                  const grouped = {};
                  apiVilla.rates.forEach(rate => {
                    const key = `${rate.price}_${rate.room_count}`;
                    if (!grouped[key]) {
                      grouped[key] = {
                        price: parseFloat(rate.price),
                        room_count: rate.room_count,
                        min_stay: rate.min_stay,
                        switchover_day: rate.switchover_day,
                        periods: []
                      };
                    }
                    grouped[key].periods.push({
                      start: rate.period_start,
                      end: rate.period_end
                    });
                  });
                  
                  // Formater pour l'affichage
                  return Object.values(grouped).map(group => {
                    const nightlyPrice = group.price;
                    const weeklyPrice = nightlyPrice * 7;
                    const formattedNightlyRate = !isNaN(nightlyPrice)
                      ? `${Math.round(nightlyPrice).toLocaleString('fr-FR').replace(/\s/g, ' ')} €`
                      : 'Sur demande';
                    const formattedWeeklyRate = !isNaN(weeklyPrice) 
                      ? `${Math.round(weeklyPrice).toLocaleString('fr-FR').replace(/\s/g, ' ')} €`
                      : 'Sur demande';
                    
                    // Formater les périodes
                    const periodLabels = group.periods.map(p => {
                      const start = new Date(p.start);
                      const end = new Date(p.end);
                      return `${start.toLocaleDateString('fr-FR', {month: 'short', year: 'numeric'})} - ${end.toLocaleDateString('fr-FR', {month: 'short', year: 'numeric'})}`;
                    }).join(', ');
                    
                    return {
                      period: `${group.room_count} chambres`,
                      periodDates: periodLabels,
                      nightlyRate: formattedNightlyRate,
                      weeklyRate: formattedWeeklyRate,
                      features: [
                        `Séjour minimum: ${group.min_stay || 3} nuits`,
                        group.switchover_day ? `Arrivée: ${group.switchover_day}` : 'Arrivée flexible'
                      ]
                    };
                  });
                })()
              : [{
                  period: 'Toute l\'année',
                  periodDates: '',
                  nightlyRate: 'Sur demande',
                  weeklyRate: 'Sur demande',
                  features: ['Prix sur mesure', 'Disponibilité à confirmer']
                }],
            
            // Services INCLUS - de la BDD
            services: apiVilla.included_services && apiVilla.included_services.length > 0
              ? apiVilla.included_services.map(s => s.item)
              : [],
            
            // Services NON INCLUS - de la BDD
            notIncludedServices: apiVilla.not_included_services && apiVilla.not_included_services.length > 0
              ? apiVilla.not_included_services.map(s => s.item)
              : [],
            
            // Conditions de location - de la BDD
            rentalConditions: apiVilla.rental_conditions && apiVilla.rental_conditions.length > 0
              ? apiVilla.rental_conditions.map(c => c.condition_text)
              : [],
            
            // Conditions de réservation - de la BDD
            reservationConditions: apiVilla.reservation_conditions && apiVilla.reservation_conditions.length > 0
              ? apiVilla.reservation_conditions.map(c => c.condition_text)
              : [],
            
            // Conditions d'annulation - de la BDD
            cancelConditions: apiVilla.cancel_conditions && apiVilla.cancel_conditions.length > 0
              ? apiVilla.cancel_conditions.map(c => c.condition_text)
              : [],
            
            // Conditions de séjour (switchover_day et min_stay) - depuis rates
            switchoverDay: apiVilla.rates && apiVilla.rates.length > 0 && apiVilla.rates[0].switchover_day
              ? apiVilla.rates[0].switchover_day
              : '',
            minStay: apiVilla.rates && apiVilla.rates.length > 0 && apiVilla.rates[0].min_stay
              ? apiVilla.rates[0].min_stay
              : null,
            
            // Expériences - DÉSACTIVÉ (pas de données BDD)
            experiences: [],
            
            curatedExperiences: [
              {
                title: 'Chef privé',
                description: 'Cuisine gastronomique personnalisée',
                icon: Award
              },
              {
                title: 'Service de conciergerie',
                description: 'Disponible 24/7 pour tous vos besoins',
                icon: Phone
              },
              {
                title: 'Transport privé',
                description: 'Véhicule avec chauffeur',
                icon: Car
              }
            ],
            
            // Concierge
            concierge: {
              name: 'Christopher Warren',
              avatar: '/photochris.png',
              specialty: 'Concierge de luxe',
              experience: '15 ans d\'expérience',
              description: 'Spécialiste des séjours d\'exception, je suis à votre disposition pour personnaliser votre expérience et répondre à toutes vos demandes.',
              languages: ['Français', 'Anglais'],
              contact: {
                phone: '+1 786 840 0745',
                email: 'chris@priviacollection.com',
                whatsapp: '+1 786 840 0745'
              }
            },
            
            // Détails de localisation
            locationDetails: {
              coordinates: `${apiVilla.latitude}, ${apiVilla.longitude}`,
              nearbyAttractions: apiVilla.distances && apiVilla.distances.length > 0
                ? apiVilla.distances.map(d => {
                    // Parser "Centre Ville (4 KM)" en { name: "Centre Ville", distance: "4 km" }
                    const match = d.distance_info.match(/^(.+?)\s*\((.+?)\)$/);
                    if (match) {
                      return {
                        name: match[1].trim(),
                        distance: match[2].toLowerCase().replace('km', 'km')
                      };
                    }
                    return { name: d.distance_info, distance: '' };
                  })
                : [
                    { name: `Centre de ${apiVilla.city}`, distance: 'À proximité' },
                    { name: 'Commerces', distance: 'À proximité' },
                    { name: 'Restaurants', distance: 'À proximité' }
                  ],
              accessibility: apiVilla.city ? `Accès facile depuis ${apiVilla.city} et les principales voies` : 'Accès facile depuis les principales routes'
            },
            
            rating: 4.9,
            reviews: Math.floor(Math.random() * 50) + 10
          };
          
          setVilla(formattedVilla);
    } else {
          setError('Villa non trouvée');
          setTimeout(() => navigate('/search'), 2000);
        }
      } catch (err) {
        console.error('Error loading villa:', err);
        setError('Erreur lors du chargement de la villa');
        setTimeout(() => navigate('/search'), 2000);
      } finally {
        setLoading(false);
      }
    };
    
    fetchVillaDetails();
  }, [villaId, navigate]);

  // Charger les disponibilités depuis l'API Villanovo
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!villaId) return;
      
      setAvailability(prev => ({ ...prev, loading: true, error: null }));
      
      try {
        const response = await villasService.getVillaAvailability(villaId);
        
        if (response.success && response.data && response.data.icalData) {
          const bookedDates = getBookedDates(response.data.icalData);
          setAvailability({
            loading: false,
            bookedDates,
            error: null
          });
        } else {
          setAvailability({
            loading: false,
            bookedDates: [],
            error: 'Aucune donnée de disponibilité'
          });
        }
      } catch (error) {
        console.error('Erreur lors du chargement des disponibilités:', error);
        setAvailability({
          loading: false,
          bookedDates: [],
          error: 'Impossible de charger les disponibilités'
        });
      }
    };
    
    fetchAvailability();
  }, [villaId]);

  // Surveiller les changements de initialShowBooking
  useEffect(() => {
    if (initialShowBooking) {
      setShowBooking(true);
    }
  }, [initialShowBooking]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-white">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-amber-600 animate-spin mx-auto mb-6" />
          <p className="text-xl text-gray-600 font-light">Chargement de la villa...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-white">
        <div className="text-center">
          <X className="w-16 h-16 text-red-400 mx-auto mb-6" />
          <p className="text-xl text-gray-600 font-light mb-4">{error}</p>
          <p className="text-sm text-gray-500">Redirection vers la recherche...</p>
        </div>
      </div>
    );
  }

  if (!villa) {
    return null;
  }

  // Icônes par type de propriété
  const propertyTypeIcons = {
    'Villa': Home,
    'Chalet': Home,
    'Maison': Home,
    'Château': Crown,
    'Appartement': Home,
    'Hotel': Home,
    'Lodge': Home
  };
  
  const PropertyIcon = propertyTypeIcons[villa.propertyType] || Home;

  const nextImage = () => {
    setCurrentImageIndex((prev) => {
      const next = (prev + 1) % villa.gallery.length;
      // Précharger l'image suivante si pas encore fait
      if (!imagesPreloaded) {
        const img = new Image();
        img.src = villa.gallery[(next + 1) % villa.gallery.length];
      }
      return next;
    });
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => {
      const previous = (prev - 1 + villa.gallery.length) % villa.gallery.length;
      // Précharger l'image précédente si pas encore fait
      if (!imagesPreloaded) {
        const img = new Image();
        img.src = villa.gallery[(previous - 1 + villa.gallery.length) % villa.gallery.length];
      }
      return previous;
    });
  };

  const calculateNights = () => {
    if (selectedDates.checkIn && selectedDates.checkOut) {
      const checkIn = new Date(selectedDates.checkIn);
      const checkOut = new Date(selectedDates.checkOut);
      const timeDiff = checkOut.getTime() - checkIn.getTime();
      return Math.ceil(timeDiff / (1000 * 3600 * 24));
    }
    return 7; // Default 1 week
  };

  // Garder currentSeason pour l'affichage dans la sidebar
  const currentSeason = villa.pricingSeasons[selectedSeason];
  
  // Calculer le prix total en fonction des dates et des tarifs de la BDD
  const calculateTotalPrice = () => {
    if (!selectedDates.checkIn || !selectedDates.checkOut || !villa.rawRates || villa.rawRates.length === 0) {
      // Prix par défaut si pas de dates ou pas de tarifs
  const basePrice = parseInt(currentSeason.weeklyRate.replace(/[^0-9]/g, ''));
      return {
        total: calculateNights() * (basePrice / 7),
        perNight: basePrice / 7,
        nights: calculateNights()
      };
    }

    const checkIn = new Date(selectedDates.checkIn);
    const checkOut = new Date(selectedDates.checkOut);
    const nights = calculateNights();
    
    let totalPrice = 0;
    let currentDate = new Date(checkIn);
    
    // Pour chaque nuit, trouver le tarif applicable
    for (let i = 0; i < nights; i++) {
      const rate = villa.rawRates.find(r => {
        const start = new Date(r.period_start);
        const end = new Date(r.period_end);
        return currentDate >= start && currentDate <= end;
      });
      
      if (rate) {
        totalPrice += parseFloat(rate.price);
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return {
      total: totalPrice,
      perNight: totalPrice / nights,
      nights
    };
  };

  const priceCalculation = calculateTotalPrice();
  const totalPrice = priceCalculation.total;
  const pricePerNight = priceCalculation.perNight;

  return (
    <div className="min-h-screen">

      {/* Hero Gallery Section */}
      <section className="relative h-screen overflow-hidden -mt-0">
        <div className="relative w-full h-full">
          <img
            src={villa.gallery[currentImageIndex]}
            alt={`${villa.name} - Image ${currentImageIndex + 1}`}
            className="w-full h-full object-cover transition-all duration-500 ease-out"
            loading="eager"
            decoding="async"
          />
          
          {/* Indicateur de préchargement (discret) */}
          {!imagesPreloaded && (
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-black/40 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Chargement des images...
            </div>
          )}
          
          {/* Gallery Navigation */}
          {villa.gallery.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-black/20 hover:bg-black/30 backdrop-blur-sm text-white p-3 rounded-full border border-white/20 transition-all duration-200 z-30 cursor-pointer"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-black/20 hover:bg-black/30 backdrop-blur-sm text-white p-3 rounded-full border border-white/20 transition-all duration-200 z-30 cursor-pointer"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Gallery Actions */}
          <div className="absolute top-6 right-6 flex gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowGallery(true)}
              className="bg-black/20 hover:bg-black/30 backdrop-blur-sm text-white p-3 rounded-full border border-white/20"
            >
              <Grid3X3 className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              className="bg-black/20 hover:bg-black/30 backdrop-blur-sm text-white p-3 rounded-full border border-white/20"
            >
              <Camera className="w-5 h-5" />
            </Button>
          </div>

          {/* Gallery Indicators */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
            {villa.gallery.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentImageIndex 
                    ? 'bg-white shadow-lg scale-125' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
              />
            ))}
          </div>

          {/* Overlay Content */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-end">
                {/* Villa Information */}
                <div className="lg:col-span-2 text-white">
                  <div className="flex items-center gap-2 mb-4 opacity-90">
                    <MapPin className="w-5 h-5" />
                    <span className="text-lg font-light">{villa.location}</span>
                  </div>
                  
                  <h1 
                    className="text-5xl md:text-6xl font-light mb-4 tracking-wide leading-tight"
                    style={{ 
                      fontFamily: 'Playfair Display, serif',
                      textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                    }}
                  >
                    {villa.name}
                  </h1>
                  
                  <p 
                    className="text-xl opacity-90 italic mb-6 max-w-2xl font-light"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {villa.emotionalTagline}
                  </p>
                  
                  <div className="flex items-center gap-6 text-lg">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      <span>{villa.capacity}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Bed className="w-5 h-5" />
                      <span>{villa.bedrooms} chambres</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Bath className="w-5 h-5" />
                      <span>{villa.bathrooms} salles de bain</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Home className="w-5 h-5" />
                      <span>{villa.surface}</span>
                    </div>
                  </div>
                </div>
                
                {/* Pricing Card */}
                <div className="lg:col-span-1">
                  <Card className="bg-white/10 backdrop-blur-md border-white/20 p-6 text-white">
                    <div className="text-center">
                      <div className="text-3xl font-light mb-1">{currentSeason.weeklyRate}</div>
                      <div className="text-lg opacity-80 mb-2">{villa.priceSubtext}</div>
                      {currentSeason.nightlyRate && (
                        <div className="text-sm opacity-70 mb-3">
                          {currentSeason.nightlyRate} / nuit
                        </div>
                      )}
                      <div className="text-sm opacity-70 mb-4">{currentSeason.period}</div>
                      {currentSeason.periodDates && (
                        <div className="text-xs opacity-60 mb-4">{currentSeason.periodDates}</div>
                      )}
                      <Button 
                        onClick={() => setShowBooking(true)}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 rounded-lg font-medium transition-all duration-300"
                      >
                        Vérifier Disponibilité
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Villa Details Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-50/50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-16">
              
              {/* Villa Story */}
              <div>
                <h2 
                  className="text-4xl font-light mb-8 text-gray-800"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  L'Histoire de cette Villa
                </h2>
                
                <div 
                  className="text-base text-gray-700 leading-relaxed space-y-4"
                  style={{ 
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  <style dangerouslySetInnerHTML={{
                    __html: `
                      .villa-story-content strong {
                        font-weight: 600;
                        color: #1f2937;
                        font-size: 1.25rem;
                        display: block;
                        margin-top: 1.5rem;
                        margin-bottom: 0.75rem;
                        font-family: 'Inter', sans-serif;
                      }
                      .villa-story-content strong:first-child {
                        margin-top: 0;
                      }
                      .villa-story-content br {
                        display: block;
                        content: "";
                        margin-bottom: 0.25rem;
                      }
                      .villa-story-content p {
                        margin-bottom: 0.5rem;
                      }
                    `
                  }} />
                  <div 
                    className="villa-story-content"
                    dangerouslySetInnerHTML={{ __html: villa.storyHTML || villa.story }}
                  />
                </div>
              </div>

              {/* Experiences Signature - Masqué si pas de données BDD */}
              {villa.experiences.length > 0 && (
              <div>
                <h3 
                  className="text-3xl font-light mb-12 text-gray-800 text-center"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Expériences Signature
                </h3>
                
                <div className="max-w-4xl mx-auto">
                  <div className="space-y-8">
                    {villa.experiences.map((experience, index) => (
                      <div key={index} className="group">
                        <div className="flex items-start gap-6 py-6 border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors duration-300 rounded-lg px-4">
                          
                          <div className="flex-shrink-0">
                            <div className="w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-600 font-light text-sm group-hover:border-amber-300 group-hover:text-amber-600 transition-colors duration-300">
                              {experience.time}
                            </div>
                          </div>
                          
                          <div className="flex-1 pt-1">
                            <h4 
                              className="text-lg font-light mb-2 text-gray-800 group-hover:text-gray-900 transition-colors duration-300"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              {experience.title}
                            </h4>
                            <p 
                              className="text-gray-600 leading-relaxed font-light text-sm"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              {experience.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-8">
              
              {/* Concierge Card */}
              <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100 sticky top-24">
                <div className="text-center mb-6">
                  <img
                    src={villa.concierge.avatar}
                    alt={villa.concierge.name}
                    className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-4 border-white shadow-lg"
                  />
                  <h3 
                    className="text-xl font-semibold text-gray-800 mb-2"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {villa.concierge.name}
                  </h3>
                  <p className="text-amber-700 text-sm font-medium mb-2">
                    {villa.concierge.specialty}
                  </p>
                  <p className="text-xs text-gray-600 mb-2">
                    {villa.concierge.experience}
                  </p>
                  <div className="flex justify-center gap-1 mb-4">
                    {villa.concierge.languages.map((lang, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs px-2 py-1">
                        {lang}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed italic">
                    {villa.concierge.description}
                  </p>
                </div>
                
                <div className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full flex items-center gap-2 border-amber-200 text-amber-700 hover:bg-amber-100"
                  >
                    <Phone className="w-4 h-4" />
                    Appeler maintenant
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full flex items-center gap-2 border-amber-200 text-amber-700 hover:bg-amber-100"
                  >
                    <Mail className="w-4 h-4" />
                    Message privé
                  </Button>
                </div>
              </Card>

              {/* Conditions de Séjour */}
              <StayConditions 
                switchoverDay={villa.switchoverDay}
                minStay={villa.minStay}
                maxGuests={villa.capacity}
              />

              {/* Villa Specs */}
              <Card className="p-6">
                <h4 className="text-lg font-semibold mb-4 text-gray-800">Caractéristiques</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Surface habitable</span>
                    <span className="font-medium">{villa.surface}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Terrain</span>
                    <span className="font-medium">{villa.terrain}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Capacité</span>
                    <span className="font-medium">{villa.capacity}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Chambres</span>
                    <span className="font-medium">{villa.bedrooms}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Salles de bain</span>
                    <span className="font-medium">{villa.bathrooms}</span>
                  </div>
                </div>
              </Card>

              {/* Location Info */}
              <Card className="p-6">
                <h4 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Localisation
                </h4>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-500">Coordonnées</span>
                    <p className="text-sm font-mono">{villa.locationDetails.coordinates}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 mb-2 block">Points d'intérêt</span>
                    <div className="space-y-1">
                      {villa.locationDetails.nearbyAttractions.map((attraction, idx) => (
                        <div key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                          <div className="w-1 h-1 bg-gray-400 rounded-full" />
                          <span>{attraction.name} - {attraction.distance}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Shield className="w-4 h-4" />
                      {villa.locationDetails.accessibility}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Tarifs par Saison - Pleine largeur */}
      <section className="py-16 px-6 bg-gradient-to-br from-slate-50/50 to-white">
        <div className="max-w-7xl mx-auto">
          <h3 
            className="text-3xl font-light mb-12 text-gray-800 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Tarifs par Saison
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {villa.pricingSeasons.map((season, index) => (
              <Card 
                key={index}
                className={`p-6 cursor-pointer transition-all duration-300 hover:shadow-xl ${
                  selectedSeason === index 
                    ? 'ring-2 ring-amber-500 shadow-lg bg-amber-50/50' 
                    : 'hover:shadow-lg'
                }`}
                onClick={() => setSelectedSeason(index)}
              >
                <div className="text-center">
                  <h4 className="text-xl font-semibold mb-2 text-gray-800">
                    {season.period}
                  </h4>
                  {season.periodDates && (
                    <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                      {season.periodDates}
                    </p>
                  )}
                  <div className="text-2xl font-light text-amber-600 mb-1">
                    {season.weeklyRate}
                  </div>
                  <div className="text-sm text-gray-500 mb-1">
                    par semaine
                  </div>
                  {season.nightlyRate && (
                    <div className="text-sm text-gray-400 mb-4">
                      {season.nightlyRate} / nuit
                    </div>
                  )}
                  
                  {/* Badges visuels pour conditions */}
                  <div className="space-y-2 mt-4">
                    {season.features.map((feature, idx) => {
                      // Détecter si c'est une condition importante (switchover ou min stay)
                      const isMinStay = feature.includes('Séjour minimum');
                      const isSwitchover = feature.includes('Arrivée:') && !feature.includes('flexible');
                      
                      if (isMinStay || isSwitchover) {
                        return (
                          <div key={idx} className="flex items-center justify-center">
                            <Badge 
                              variant="default" 
                              className={`${
                                isSwitchover 
                                  ? 'bg-amber-600 hover:bg-amber-700' 
                                  : 'bg-blue-600 hover:bg-blue-700'
                              } text-white font-medium text-xs px-3 py-1`}
                            >
                              {feature}
                            </Badge>
                          </div>
                        );
                      }
                      
                      // Autres features en mode normal
                      return (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                          <span>{feature}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Calendrier de Disponibilité - Pleine largeur */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 
            className="text-3xl font-light mb-12 text-gray-800 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Calendrier de Disponibilité
          </h3>
          
          {availability.error && !availability.loading && (
            <div className="text-center text-amber-600 mb-6 p-4 bg-amber-50 rounded-lg">
              <AlertCircle className="w-6 h-6 mx-auto mb-2" />
              <p>{availability.error}</p>
              <p className="text-sm text-gray-600 mt-2">
                Les disponibilités seront mises à jour prochainement
              </p>
            </div>
          )}
          
          <AvailabilityCalendar 
            bookedDates={availability.bookedDates} 
            loading={availability.loading}
            switchoverDay={villa.switchoverDay}
            minStay={villa.minStay}
            rates={villa.rawRates || []}
            villaId={villa.id}
          />
          
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>Les disponibilités sont mises à jour en temps réel via l'API Villanovo</p>
            <p className="mt-2">Pour toute réservation, veuillez contacter notre concierge</p>
          </div>
        </div>
      </section>

      {/* Équipements & Services - Pleine largeur */}
      {(villa.amenities.length > 0 || villa.services.length > 0 || (villa.notIncludedServices && villa.notIncludedServices.length > 0)) && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <h3 
              className="text-3xl font-light mb-12 text-gray-800 text-center"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Équipements & Services
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              
              {/* Amenities */}
              {villa.amenities.length > 0 && (
                <div>
                  <h4 
                    className="text-xl font-light mb-6 text-gray-700 border-b border-gray-200 pb-3"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Équipements
                  </h4>
                  
                  <div className="space-y-3">
                    {villa.amenities.map((amenity, index) => {
                      const amenityText = typeof amenity === 'string' ? amenity : amenity.name;
                      return (
                        <div key={index} className="flex items-center gap-3 py-2 group">
                          <div className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-amber-500 transition-colors duration-200" />
                          <span className="text-gray-600 font-light group-hover:text-gray-800 transition-colors duration-200">
                            {amenityText}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Services Inclus */}
              {villa.services.length > 0 && (
                <div>
                  <h4 
                    className="text-xl font-light mb-6 text-gray-700 border-b border-gray-200 pb-3"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Services Inclus
                  </h4>
                  
                  <div className="space-y-3">
                    {villa.services.map((service, index) => (
                      <div key={index} className="flex items-center gap-3 py-2 group">
                        <div className="w-1 h-1 bg-green-500 rounded-full group-hover:bg-green-600 transition-colors duration-200" />
                        <span className="text-gray-600 font-light group-hover:text-gray-800 transition-colors duration-200">
                          {service}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Services NON Inclus */}
              {villa.notIncludedServices && villa.notIncludedServices.length > 0 && (
                <div>
                  <h4 
                    className="text-xl font-light mb-6 text-gray-700 border-b border-gray-200 pb-3"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Non Inclus
                  </h4>
                  
                  <div className="space-y-3">
                    {villa.notIncludedServices.map((service, index) => (
                      <div key={index} className="flex items-center gap-3 py-2 group">
                        <div className="w-1 h-1 bg-red-400 rounded-full group-hover:bg-red-500 transition-colors duration-200" />
                        <span className="text-gray-600 font-light group-hover:text-gray-800 transition-colors duration-200">
                          {service}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm">
          <div className="flex flex-col h-full">
            <div className="flex justify-between items-center p-6">
              <h3 
                className="text-2xl font-light text-white"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {villa.name} - Galerie
              </h3>
              <Button
                variant="ghost"
                onClick={() => setShowGallery(false)}
                className="text-white hover:bg-white/10 p-2"
              >
                <X className="w-6 h-6" />
              </Button>
            </div>
            
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-6xl mx-auto">
                {villa.gallery.map((image, index) => (
                  <div 
                    key={index}
                    className="aspect-square cursor-pointer overflow-hidden rounded-lg hover:scale-105 transition-transform duration-300"
                    onClick={() => {
                      setCurrentImageIndex(index);
                      setShowGallery(false);
                    }}
                  >
                    <img 
                      src={image}
                      alt={`${villa.name} - ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-lg bg-white p-8 relative max-h-[90vh] overflow-y-auto">
            <Button
              variant="ghost"
              onClick={() => setShowBooking(false)}
              className="absolute top-4 right-4 p-2"
            >
              <X className="w-5 h-5" />
            </Button>
            
            <h3 
              className="text-2xl font-semibold mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Réserver {villa.name}
            </h3>
            
            <div className="space-y-6">
              {/* Calendrier de disponibilité intégré */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Sélectionnez vos dates
                </label>
                <AvailabilityCalendar 
                  bookedDates={availability.bookedDates} 
                  loading={availability.loading}
                  switchoverDay={villa.switchoverDay}
                  minStay={villa.minStay}
                  rates={villa.rawRates || []}
                  villaId={villa.id}
                  compact={true}
                  onDateSelect={(dates) => setSelectedDates(dates)}
                />
              </div>
              
              {/* Guests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre d'invités
                </label>
                <select
                  value={guests}
                  onChange={(e) => setGuests(parseInt(e.target.value))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} {i + 1 === 1 ? 'personne' : 'personnes'}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Pricing Summary */}
              <div className="border-t pt-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    {priceCalculation.nights} nuits × {Math.round(pricePerNight).toLocaleString('fr-FR')}€/nuit
                  </span>
                  <span className="font-semibold">
                    {Math.round(totalPrice).toLocaleString('fr-FR')}€
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Services inclus</span>
                  <span className="text-green-600">Inclus</span>
                </div>
                
                <div className="flex justify-between items-center text-lg font-semibold border-t pt-3">
                  <span>Total</span>
                  <span>{Math.round(totalPrice).toLocaleString('fr-FR')}€</span>
                </div>
              </div>
              
              <Button 
                className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white py-3 rounded-lg font-semibold text-lg mt-6"
              >
                Confirmer la Réservation
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Conditions & Informations Importantes - EN BAS avec accordéons */}
      {(villa.rentalConditions?.length > 0 || villa.reservationConditions?.length > 0 || villa.cancelConditions?.length > 0) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-gray-50 to-slate-50">
          <h3 
            className="text-3xl font-light mb-8 text-gray-800 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Conditions & Informations Importantes
          </h3>
          
          <div className="space-y-4 max-w-4xl mx-auto">
            
            {/* Conditions de location - Accordéon */}
            {villa.rentalConditions?.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <button
                  onClick={() => toggleAccordion('rental')}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center gap-3">
                    <Home className="w-5 h-5 text-amber-600" />
                    <h4 
                      className="text-lg font-semibold text-gray-800"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Conditions de Location
                    </h4>
                    <Badge variant="secondary" className="ml-2">
                      {villa.rentalConditions.length}
                    </Badge>
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                      openAccordions.rental ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                {openAccordions.rental && (
                  <div className="px-5 pb-5 space-y-2 border-t border-gray-100">
                    {villa.rentalConditions.map((condition, index) => (
                      <div 
                        key={index} 
                        className="flex items-start gap-3 py-3 px-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors duration-200"
                      >
                        <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-sm text-gray-700 leading-relaxed">{condition}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Conditions de réservation - Accordéon */}
            {villa.reservationConditions?.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <button
                  onClick={() => toggleAccordion('reservation')}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-amber-600" />
                    <h4 
                      className="text-lg font-semibold text-gray-800"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Conditions de Réservation
                    </h4>
                    <Badge variant="secondary" className="ml-2">
                      {villa.reservationConditions.length}
                    </Badge>
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                      openAccordions.reservation ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                {openAccordions.reservation && (
                  <div className="px-5 pb-5 space-y-2 border-t border-gray-100">
                    {villa.reservationConditions.map((condition, index) => (
                      <div 
                        key={index} 
                        className="flex items-start gap-3 py-3 px-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                      >
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-sm text-gray-700 leading-relaxed">{condition}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Conditions d'annulation - Accordéon */}
            {villa.cancelConditions?.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <button
                  onClick={() => toggleAccordion('cancel')}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <h4 
                      className="text-lg font-semibold text-gray-800"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Conditions d'Annulation
                    </h4>
                    <Badge variant="secondary" className="ml-2">
                      {villa.cancelConditions.length}
                    </Badge>
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                      openAccordions.cancel ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                {openAccordions.cancel && (
                  <div className="px-5 pb-5 space-y-2 border-t border-gray-100">
                    {villa.cancelConditions.map((condition, index) => (
                      <div 
                        key={index} 
                        className="flex items-start gap-3 py-3 px-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors duration-200"
                      >
                        <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-sm text-gray-700 leading-relaxed">{condition}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      )}

      {/* Concierge Chat */}
      <ConciergeChat 
        triggerMessage={`${villa.id}_view`}
        onClose={() => {}}
      />
    </div>
  );
};

export default VillaDetail;